/**
 * Homepage promotional offers — restored after the full <Music/> catalogue
 * was removed from `/`.
 *
 * Two lightweight banners:
 *   1. 5 FOR 5 — 5% OFF (leads to Premium collection selection flow)
 *   2. ALL PREMIUM — 10% OFF (one-click "Add All Remaining Premium" CTA)
 *
 * Performance contract (verified):
 *   • ZERO calls to `/api/tracks` on the homepage.
 *   • Uses ONE lightweight promo-config endpoint (`/api/bundle/quote`).
 *   • The one-click CTA calls `/api/premium/remaining` ONLY on click —
 *     not on page load — and only after the user is authenticated.
 *   • No track covers, previews, audio, or filters are loaded.
 */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sparkles, ShoppingBag, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useCart } from "@/lib/CartContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";

export default function HomePromoOffers() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addMany, open: openCart } = useCart();
  const { lang } = useLocale();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/bundle/quote")
      .then((r) => { if (alive) setQuote(r.data || null); })
      .catch(() => { if (alive) setQuote(null); });
    return () => { alive = false; };
  }, []);

  const onAddAllRemaining = useCallback(async () => {
    if (adding) return;
    // Logged-out: send to sign-in first — client cannot compute ownership.
    if (!user) {
      toast(t("promotions.bundle.signInRequired", {
        defaultValue: "Sign in to add the remaining Premium collection to your cart.",
      }));
      navigate(localisedTo("/login?next=/", lang));
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.get("/premium/remaining");
      const tracks = Array.isArray(data?.remaining_tracks) ? data.remaining_tracks : [];
      if (tracks.length === 0) {
        toast.success(t("promotions.bundle.alreadyComplete", {
          defaultValue: "You already own the complete Premium collection.",
        }));
        return;
      }
      const added = addMany(tracks);
      if (added > 0) {
        toast.success(t("promotions.bundle.addedToast", {
          defaultValue: "Added {{count}} Premium tracks — 10% ALL PREMIUM applied at checkout",
          count: added,
        }));
      } else {
        toast(t("promotions.bundle.alreadyInCart", {
          defaultValue: "All remaining Premium tracks are already in your cart.",
        }));
      }
      openCart();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("promotions.bundle.addFailed", {
        defaultValue: "Couldn't add the collection. Your cart was not changed. Please try again.",
      }));
    } finally { setAdding(false); }
  }, [adding, user, addMany, openCart, navigate, lang, t]);

  if (!quote) return null;
  const showFF = !!quote?.five_for_five?.enabled;
  // `enabled` on the bundle quote is server-authoritative — it will
  // return false when either the admin disabled the bundle OR the
  // remaining Premium count is below the ALL PREMIUM min threshold
  // (default 10).  In that latter case we hide the 10% banner and let
  // the 5 FOR 5 offer speak for itself.
  const showBundle = !!quote?.enabled;
  if (!showFF && !showBundle) return null;

  const ffPct = Number(quote?.five_for_five?.discount_percent ?? 5);
  const ffMin = Number(quote?.five_for_five?.min_tracks ?? 5);
  const bundlePct = Number(quote?.discount_percent ?? 10);
  const remainingCount = Number(quote?.remaining_count ?? 0);
  const bundleMinRemaining = Number(quote?.min_remaining_tracks ?? 10);
  // Ownership state — only meaningful for signed-in users.  For
  // anonymous visitors we still show the CTA (they'll be routed to
  // sign-in on click).  "Complete" state only shows when signed-in AND
  // remaining == 0 AND the bundle is admin-enabled.
  const bundleComplete = Boolean(user)
    && Boolean(quote?.settings_enabled)
    && remainingCount === 0;

  return (
    <section
      id="homepage-music-store"
      data-testid="homepage-music-store"
      className="bg-black text-white py-16 sm:py-20 px-6 md:px-10"
    >
      <div className="max-w-[1400px] mx-auto">
        <header className="mb-8 sm:mb-10">
          <p className="font-mono text-[11px] tracking-[0.3em] text-red-brand mb-2">
            // {t("music.kicker").replace(/^\/\/\s*/, "")}
          </p>
          <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl leading-tight uppercase tracking-tight">
            {t("music.title")}
          </h2>
          <p className="text-white/60 text-sm sm:text-base mt-3 max-w-2xl">
            {t("home.musicStore.lead", {
              defaultValue: "Two active promotions. Bundle savings while they last.",
            })}
          </p>
        </header>

        <div
          data-testid="homepage-promo-strip"
          className={`grid grid-cols-1 ${(showFF && showBundle) ? "md:grid-cols-2" : ""} gap-4`}
        >
          {showFF && (
            <div
              data-testid="promo-five-for-five"
              className="hairline rounded-2xl bg-white/[0.02] border-red-brand/25 hover:border-red-brand/60 transition-colors p-5"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 font-mono text-[10px] tracking-[0.28em] text-black bg-red-brand rounded-full px-2.5 py-1">
                  {ffPct}% OFF
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand/90 uppercase">
                    {t("promotions.strip.fiveForFive.kicker")}
                  </div>
                  <div className="font-anton text-2xl sm:text-3xl leading-tight mt-1">
                    {t("promotions.strip.fiveForFive.headline", { count: ffMin, percent: ffPct })}
                  </div>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                {t("promotions.strip.fiveForFive.body", { count: ffMin, percent: ffPct })}
              </p>
              <button
                data-testid="promo-ff-cta"
                onClick={() => navigate(localisedTo("/music/premium", lang))}
                className="btn-ghost !py-2 !px-4 text-xs inline-flex items-center gap-2"
              >
                <Sparkles size={12} />
                {t("promotions.strip.fiveForFive.cta", { defaultValue: "Choose 5 Premium tracks" })}
              </button>
            </div>
          )}

          {showBundle && (
            <div
              data-testid="promo-all-premium"
              className="hairline rounded-2xl bg-white/[0.02] border-red-brand/25 hover:border-red-brand/60 transition-colors p-5"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 font-mono text-[10px] tracking-[0.28em] text-black bg-red-brand rounded-full px-2.5 py-1">
                  {bundlePct}% OFF
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand/90 uppercase">
                    {t("promotions.strip.bundle.kicker")}
                  </div>
                  <div className="font-anton text-2xl sm:text-3xl leading-tight mt-1">
                    {t("promotions.strip.bundle.headline", { percent: bundlePct })}
                  </div>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                {t("promotions.strip.bundle.body", { percent: bundlePct })}
              </p>

              {bundleComplete ? (
                <div
                  data-testid="promo-bundle-complete"
                  className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-4 py-2"
                >
                  <Check size={12} />
                  {t("promotions.bundle.complete", {
                    defaultValue: "Premium Collection Complete",
                  })}
                </div>
              ) : (
                <button
                  data-testid="promo-bundle-add-all"
                  onClick={onAddAllRemaining}
                  disabled={adding}
                  className="btn-primary !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {adding ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                  {adding
                    ? t("promotions.bundle.addingCollection", { defaultValue: "Adding collection…" })
                    : t("promotions.bundle.addAllRemaining", {
                        defaultValue: "Add All Remaining Premium",
                      })}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
